import { useMemo } from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';
import {
  collection,
  query,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  limit,
  where,
  type Query,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { COLLECTIONS, splitPath } from '@elka/firebase';
import { useAuth } from '../../auth/hooks/useAuth';
import type { Activity, ActivityInput } from '@elka/types';

export interface UseActivitiesFilter {
  contactId?: string;
  companyId?: string;
  dealId?: string;
}

export function useActivities(limitCount?: number, filter?: UseActivitiesFilter) {
  const { user } = useAuth();

  // Server-side filtering by linked entity — detail pages used to pull the whole
  // collection and filter client-side, causing "אין פעילויות" to flash on every nav
  // until the entire collection loaded.
  // Memoized on the filter/limit primitives so a fresh query() object every render
  // doesn't churn the useCollection subscription (passing `filter` as an unstable
  // object literal from callers made this re-subscribe on every parent render).
  const contactId = filter?.contactId;
  const dealId = filter?.dealId;
  const companyId = filter?.companyId;
  const activitiesQuery = useMemo<Query<DocumentData>>(() => {
    const colRef = collection(db, ...splitPath(COLLECTIONS.crm.activities));
    let q: Query<DocumentData>;
    if (contactId) {
      q = query(colRef, where('contactId', '==', contactId), orderBy('createdAt', 'desc'));
    } else if (dealId) {
      q = query(colRef, where('dealId', '==', dealId), orderBy('createdAt', 'desc'));
    } else if (companyId) {
      q = query(colRef, where('companyId', '==', companyId), orderBy('createdAt', 'desc'));
    } else {
      q = query(colRef, orderBy('createdAt', 'desc'));
    }
    return limitCount ? query(q, limit(limitCount)) : q;
  }, [contactId, dealId, companyId, limitCount]);

  const [snapshot, loading, error] = useCollection(activitiesQuery);

  const activities: Activity[] =
    (snapshot?.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Activity[]) || [];

  // Add activity
  const addActivity = async (data: ActivityInput) => {
    if (!user) throw new Error('לא מחובר');

    const timestamp = serverTimestamp();
    const userInfo = {
      userId: user.uid,
      createdBy: user.displayName || user.email || 'Unknown',
      createdAt: timestamp,
    };

    const docRef = await addDoc(collection(db, ...splitPath(COLLECTIONS.crm.activities)), {
      ...data,
      ...userInfo,
    });

    return docRef.id;
  };

  // Update activity
  const updateActivity = async (id: string, data: Partial<ActivityInput>) => {
    if (!user) throw new Error('לא מחובר');

    const timestamp = serverTimestamp();
    const updateInfo = {
      updatedBy: user.displayName || user.email || 'Unknown',
      updatedAt: timestamp,
    };

    await updateDoc(doc(db, ...splitPath(COLLECTIONS.crm.activities), id), {
      ...data,
      ...updateInfo,
    });
  };

  // Delete activity
  const deleteActivity = async (id: string) => {
    if (!user) throw new Error('לא מחובר');
    await deleteDoc(doc(db, ...splitPath(COLLECTIONS.crm.activities), id));
  };

  return {
    activities,
    isLoading: loading,
    error: error?.message || null,
    addActivity,
    updateActivity,
    deleteActivity,
  };
}
